"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useRef, useState } from "react";
import { FaLink } from "react-icons/fa";
import { IoMdShare } from "react-icons/io";
import { SlOptionsVertical } from "react-icons/sl";
import { differenceInMinutes, differenceInHours, differenceInDays, format } from "date-fns";
import { enUS } from "date-fns/locale";

import { Typography } from "@/components/shared/Typography";
import envConfig from "@/config/envConfig";
import { socialShareOptions } from "@/data/social";
import { useHandleClickOutside } from "@/hooks";
import { ArticleListType } from "@/types/article";
import { getArticleImageUrl, getMediaSrcSet } from "@/utils/getImageUrl";
import { getLocaleFromPathname } from "@/utils/getLocaleFromPath";
import { getLocalizedString } from "@/utils/getLocalizedString";
import { localeFromNewsLanguage } from "@/utils/localeFromNewsLanguage";
import { getNewsDetailsUrl } from "@/utils/getNewsDetailsUrl";
import { useMessage } from "../providers/MessageProvider";
import CustomImage from "../shared/CustomImage";
import TitleTag from "../shared/TitleTag";
import { formatDate } from "@/utils/dateUtils";

interface VerticalArticleCardProps {
  variant?: "xs" | "sm" | "md" | "lg" | "sidebar" | "threeColumn" | "fourColumn";
  showCategory?: boolean;
  article: ArticleListType;
  index: number;
  showNumber?: boolean;
  showDate?: boolean;
}

const getLocalizedSocialName = (name: string, language: string): string => {
  if (language !== "te") return name;

  const socialNames: { [key: string]: string } = {
    Facebook: "ఫేస్బుక్",
    Instagram: "ఇన్స్టాగ్రామ్",
    YouTube: "యూట్యూబ్",
    X: "X",
    Whatsapp: "వాట్సాప్",
    LinkedIn: "లింక్డ్ఇన్",
    Email: "ఇమెయిల్",
  };

  return socialNames[name] || name;
};

export default function HorizontalArticleCard({
  variant = "lg",
  article,
  index,
  showCategory = false,
  showNumber = false,
  showDate = false,
}: VerticalArticleCardProps) {
  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname) || "te-IN";
  const language = (locale?.split("-")[0] || "te") as AppLocale;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const { showMessage } = useMessage();

  useHandleClickOutside(dropdownRef, () => setIsDropdownOpen(false));

  if (!article) return null;

  const linkLocale = localeFromNewsLanguage(article.language, locale);
  const { slug, title, image, categoryId, subCategoryId, publishedAt, isLiveNews, isInPrint } =
    article;

  const articleUrl = getNewsDetailsUrl(categoryId, subCategoryId, slug, linkLocale);

  const formatTimeAgo = () => {
    const now = new Date();
    const published = new Date(publishedAt);
    const diffMinutes = differenceInMinutes(now, published);
    const diffHours = differenceInHours(now, published);
    const diffDays = differenceInDays(now, published);

    if (language === "te") {
      if (diffMinutes < 60) {
        return `${diffMinutes} ${diffMinutes === 1 ? "నిమిషం" : "నిమిషాల"} క్రితం`;
      } else if (diffHours < 24) {
        return `${diffHours} ${diffHours === 1 ? "గంట" : "గంటల"} క్రితం`;
      } else if (diffDays < 7) {
        return `${diffDays} ${diffDays === 1 ? "రోజు" : "రోజుల"} క్రితం`;
      } else {
        return formatDate(published,language)
      }
    } else {
      if (diffMinutes < 60) {
        return `${diffMinutes} Min Ago`;
      } else if (diffHours < 24) {
        return `${diffHours} Hour${diffHours > 1 ? "s" : ""} Ago`;
      } else if (diffDays < 7) {
        return `${diffDays} Day${diffDays > 1 ? "s" : ""} Ago`;
      } else {
        return formatDate(published, language);
      }
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(`${envConfig?.clientUrl}${articleUrl}`);
    showMessage(
      "info",
      language === "te" ? "లింక్ క్లిప్‌బోర్డ్‌కు కాపీ చేయబడింది!" : "Link copied to clipboard!!",
    );
  };

  const textColGap = variant === "xs" ? "gap-1" : "gap-1.5";

  return (
    <div
      className={
        "flex items-start " +
        (showNumber ? "pb-6 " : "") +
        (variant === "xs"
          ? "gap-2 h-[110px]"
          : variant === "fourColumn"
            ? "gap-3 h-[100px] min-h-[100px] max-h-[100px] min-w-0 w-full overflow-hidden"
          : variant === "threeColumn"
            ? "gap-5 h-auto md:gap-6"
            : variant === "sidebar"
              ? "gap-4 h-auto"
              : variant === "sm"
                ? "gap-4 h-auto min-h-[72px] w-full min-w-0"
                : "gap-4 min-h-[142px] h-auto")
      }
    >
        <div
          className={
          `relative shrink-0 self-start ${
            variant === "xs"
              ? "aspect-[16/9]"
              : variant === "sm" || variant === "fourColumn"
                ? "aspect-[16/9.5]"
                : variant === "threeColumn"
                  ? "aspect-[16/11] min-h-[115px] md:aspect-[16/10.5] md:min-h-[125px] lg:min-h-[135px]"
                  : variant === "sidebar"
                    ? "aspect-[16/13] md:aspect-[16/12]"
                    : "aspect-[16/14] md:aspect-[16/12.75]"
          } ` +
          (variant === "xs"
            ? "w-[28%] min-w-[28%] md:w-[30%] md:min-w-[30%]"
            : variant === "sm"
              ? "w-[118px] min-w-[118px] max-w-[118px] md:w-[130px] md:min-w-[130px] md:max-w-[130px]"
              : variant === "fourColumn"
                ? "w-[35%] min-w-[35%] max-w-[35%] md:min-w-[38%] md:w-[38%] md:max-w-[38%]"
              : variant === "threeColumn"
                ? "w-[42%] min-w-[42%] md:w-[46%] md:min-w-[46%]"
                : variant === "sidebar"
                  ? "w-[38%] min-w-[38%] md:w-[42%] md:min-w-[42%]"
                  : "w-[38%] min-w-[38%] md:w-[40%] md:min-w-[40%]")
        }
      >
        <Link
          href={articleUrl}
          aria-label={`View news: ${title}`}
        >
          <div className="relative h-full w-full min-h-0 overflow-hidden rounded-lg">
            <CustomImage
              src={getArticleImageUrl(image, "ld")}
              srcSet={getMediaSrcSet(image, ["ld", "sd"])}
              sizes={
                variant === "sm"
                  ? "(max-width: 768px) 118px, 130px"
                  : "(max-width: 640px) 35vw, (max-width: 1024px) 25vw, 200px"
              }
              className={`object-cover object-${article?.focusPosition} img-zoom-hover`}
              alt={image?.alt || ""}
            />
          </div>
        </Link>

        {showNumber && (
          <div aria-live="polite" className="absolute -bottom-3 left-3">
            <div className="size-10 rounded-full overflow-hidden bg-[#FFD600] flex justify-center items-center">
              <Typography
                language={language}
                variant="h3"
                className="text-[#36454F] dark:!text-[#36454F]"
              >
                {(index + 1).toString().padStart(2, "0")}
              </Typography>
            </div>
          </div>
        )}
      </div>
      <div
        className={
          "w-full min-w-0 flex flex-col h-full min-h-0 " +
          textColGap +
          (variant === "fourColumn" ? " justify-between" : "")
        }
      >
        {/* Category and Subcategory - Above Title */}
        {showCategory && (
          <div className={"flex items-start " + (variant === "xs" ? "mb-1" : "mb-2")}>
            {categoryId && (
              <Link
                aria-live="polite"
                href={`/${linkLocale}/${categoryId?.slug}`}
                aria-label={`View all news in the category: ${
                  getLocalizedString(categoryId?.name, linkLocale) || categoryId?.slug
                }`}
              >
                <Typography
                  className="font-bold text-black uppercase dark:text-white"
                  variant="body3"
                >
                  {getLocalizedString(categoryId?.name, linkLocale) || categoryId?.slug}
                </Typography>
              </Link>
            )}
            {subCategoryId && (
              <>
                <span className="mx-3 text-gray-400 leading-none">|</span>
                <Link
                  aria-live="polite"
                  href={`/${linkLocale}/${categoryId?.slug}/${subCategoryId?.slug}`}
                  aria-label={`View all news in the sub category: ${
                    getLocalizedString(subCategoryId?.name, linkLocale) || subCategoryId?.slug
                  }`}
                >
                  <Typography className="font-medium text-gray-500 uppercase" variant="body3">
                    {getLocalizedString(subCategoryId?.name, linkLocale) || subCategoryId?.slug}
                  </Typography>
                </Link>
              </>
            )}
          </div>
        )}
        <Link
          href={articleUrl}
          aria-label={`View news: ${title}`}
          className={
            variant === "fourColumn" ? "min-h-0 min-w-0 flex-1 overflow-hidden" : "overflow-hidden"
          }
        >
          <h6
            className={
              "font-[600] leading-[1.4] text-[#36454F] dark:text-white " +
              (variant === "xs"
                ? " text-[12px] md:text-[13px] line-clamp-2 "
                : variant === "fourColumn"
                  ? " w-full min-w-0 text-[13px] md:text-[14px] line-clamp-2 "
                : variant === "sm"
                  ? " text-[15px] md:text-[13px] line-clamp-3 "
                  : variant === "sidebar" || variant === "threeColumn"
                    ? " text-[15px] md:text-lg line-clamp-3 "
                    : " text-[15px] md:text-base line-clamp-2 min-h-[40px] ")
            }
          >
            {isLiveNews &&
              publishedAt &&
              Date.now() - new Date(publishedAt).getTime() <= 12 * 60 * 60 * 1000 && (
                <TitleTag labelKey="LIVE_NEWS" />
              )}
            {isInPrint && <TitleTag labelKey="PRINT_EDITION" />}
            {title}
          </h6>
        </Link>
        {/* Time and Share Buttons - Bottom Right */}
        {showDate && publishedAt && (
          <span
            className={
              "block shrink-0 font-medium text-gray-500 dark:text-white " +
              (variant === "xs" ? "text-[10px]" : "text-xs")
            }
          >
            {formatTimeAgo()}
          </span>
        )}
      </div>
    </div>
  );
}
